/**
 * The Redeem link & QR modal, opened from the "QR Code" item in a campaign card's 3-dots menu (shown
 * only for gfg campaigns once Active). Shows the campaign's PUBLIC redeem URL (`/redeem/:redeemCode` —
 * the "Find Your Gift" email→resend page) as a downloadable QR code and a copyable link, for printing
 * on physical cards / handouts or sharing. The code is permanent (safe to print).
 */
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { Alert, Dialog, Snackbar } from "@mui/material";
import { QRCodeCanvas } from "qrcode.react";
import { useId, useRef, useState } from "react";
import GfgButton from "@/components/ui/GfgButton";
import { CONTACTS_FORM_PAPER_SX, ContactsDialogHeader } from "../contacts/ContactsDialog";

export interface RedeemQrModalProps {
  open: boolean;
  onClose: () => void;
  campaignName: string;
  /** The permanent `campaigns.redeem_code` — the `/redeem/:code` the QR + link encode. */
  redeemCode: string;
}

interface ToastOutcome {
  message: string;
  severity: "success" | "error";
}

const TOAST_MS = 3000;
const ICON_SX = { fontSize: 15 } as const;

export default function RedeemQrModal({
  open,
  onClose,
  campaignName,
  redeemCode,
}: RedeemQrModalProps) {
  const titleId = useId();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [outcome, setOutcome] = useState<ToastOutcome | null>(null);
  const [toastOpen, setToastOpen] = useState(false);

  // Same host the creator is viewing from (staging/prod), so the printed QR encodes the right
  // environment. Stable because `redeemCode` never changes — safe to print.
  const redeemUrl = `${window.location.origin}/redeem/${redeemCode}`;

  function show(message: string, severity: ToastOutcome["severity"]) {
    setOutcome({ message, severity });
    setToastOpen(true);
  }

  async function handleCopy() {
    // `navigator.clipboard` is undefined on an insecure origin and `writeText` can be blocked by a
    // permissions policy — both end in a message to act on, never a thrown error.
    try {
      await navigator.clipboard.writeText(redeemUrl);
      show("Redeem link copied", "success");
    } catch {
      show("Couldn't copy — select and copy the link manually", "error");
    }
  }

  function handleDownload() {
    // 512px internal canvas (crisp for print) → PNG data URL → anchor download.
    const url = canvasRef.current?.toDataURL("image/png");
    if (!url) return;
    const link = document.createElement("a");
    link.href = url;
    link.download = `redeem-qr-${redeemCode}.png`;
    link.click();
    show("QR code downloaded", "success");
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        aria-labelledby={titleId}
        slotProps={{ paper: { sx: CONTACTS_FORM_PAPER_SX } }}
      >
        <ContactsDialogHeader
          titleId={titleId}
          title="Redeem link & QR code"
          subtitle={campaignName}
          onClose={onClose}
          compact
        />

        <p className="mt-3 text-[13px] leading-[1.5] text-graphite">
          Share this with recipients to look up their gift — print the QR code on cards or handouts,
          or send the link. It opens the public "Find Your Gift" page for this campaign.
        </p>

        <div className="mt-4 flex justify-center">
          <div className="rounded-lg border border-smoke p-3">
            <QRCodeCanvas
              ref={canvasRef}
              value={redeemUrl}
              size={512}
              level="M"
              marginSize={4}
              title="Redeem page QR code"
              style={{ width: 200, height: 200 }}
            />
          </div>
        </div>

        <div className="mt-4 flex items-center rounded-lg border border-smoke bg-wax px-3 py-2">
          <span className="min-w-0 flex-1 truncate text-[13px] text-rodeo-black" title={redeemUrl}>
            {redeemUrl}
          </span>
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-end gap-3">
          <GfgButton
            type="button"
            variant="secondary"
            startIcon={<ContentCopyOutlinedIcon sx={ICON_SX} />}
            onClick={handleCopy}
          >
            Copy link
          </GfgButton>
          <GfgButton
            type="button"
            variant="primary"
            startIcon={<FileDownloadOutlinedIcon sx={ICON_SX} />}
            onClick={handleDownload}
          >
            Download QR
          </GfgButton>
        </div>
      </Dialog>

      <Snackbar
        open={toastOpen}
        autoHideDuration={TOAST_MS}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={outcome?.severity ?? "success"}
          variant="filled"
          onClose={() => setToastOpen(false)}
        >
          {outcome?.message}
        </Alert>
      </Snackbar>
    </>
  );
}
